import { cn } from '@/lib/utils'
import { NotificationIcon } from './NotificationIcon'
import {
  formatRelativeTime,
  notificationHref,
  notificationTitle,
} from '@/lib/notifications'
import type { AdminNotification } from '@/lib/api/admin-notifications'

interface NotificationItemProps {
  notification: AdminNotification
  onSelect: (notification: AdminNotification) => void
  className?: string
}

export function NotificationItem({ notification, onSelect, className }: NotificationItemProps) {
  const href = notificationHref(notification)
  const unread = !notification.is_read

  return (
    <button
      type="button"
      data-testid="notification-item"
      data-unread={unread ? 'true' : 'false'}
      onClick={() => onSelect(notification)}
      className={cn(
        'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors',
        'hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none',
        // A non-navigating item (e.g. a deleted account) still marks itself read,
        // so keep it clickable but drop the affordance.
        href ? 'cursor-pointer' : 'cursor-default',
        unread && 'bg-primary/[0.04]',
        className,
      )}
    >
      <NotificationIcon
        type={notification.type}
        severity={notification.severity}
        className="mt-0.5"
      />

      <div className="min-w-0 flex-1">
        <div className={cn('truncate text-sm', unread ? 'font-semibold' : 'font-medium')}>
          {notificationTitle(notification)}
        </div>
        {notification.body && (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {notification.body}
          </p>
        )}
        <span className="mt-1 block text-[11px] text-muted-foreground">
          {formatRelativeTime(notification.event_at)}
        </span>
      </div>

      {unread && (
        <span
          aria-label="Unread"
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
        />
      )}
    </button>
  )
}
